import React, { useState, useEffect, lazy, Suspense } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useLocation,
} from "react-router-dom";

import BackofficeLayout from "./backoffice/BackofficeLayout";
import AdminDashboard from "./backoffice/pages/AdminDashboard";
import CompaniesList from "./backoffice/pages/CompaniesList";
import CompanyDetail from "./backoffice/pages/CompanyDetail";
import CoursesList from "./backoffice/pages/CoursesList";
import CourseStructurePage from "./backoffice/pages/CourseStructurePage";
import TalksDetail from "./backoffice/pages/TalksDetail";
import TalksList from "./backoffice/pages/TalksList";
import RecordedClassesList from "./backoffice/pages/RecordedClassesList";
import FinalExamsAdmin from "./backoffice/pages/FinalExamsAdmin";
import UsersList from "./backoffice/pages/UsersList";
import NewsList from "./backoffice/pages/NewsList";
import CarouselList from "./backoffice/pages/CarouselList";
import WebConfigPage from "./backoffice/pages/WebConfigPage";
import InstructivoAdminPro from "./backoffice/pages/InstructivoAdminPro.jsx";
// --- IMPORTACIONES PORTAL EMPRESA ---
import CompanyDashboard from "./components/company/portal/dashboard.jsx";
import CompanyPortal from "./components/company/portal/companyportal.jsx";
import EnrollBatch from "./components/company/portal/enroll-batch.jsx";
import MyStudents from "./components/company/portal/my-students.jsx";
import UploadStudents from "./components/company/portal/upload-students.jsx";
import ProfilePortalEmp from "./components/company/portal/profilePanel.jsx";
// --- IMPORTACIONES PORTAL DOCENTE ---
import TeacherLayout from "./components/portal/docente/TeacherLayout.jsx";
import TeacherDashboard from "./components/portal/docente/TeacherDashboard.jsx";
import TeacherCourseDetail from "./components/portal/docente/TeacherCourseDetail.jsx";
import TeacherProfile from "./components/portal/docente/TeacherProfile.jsx";
// --- IMPORTACIONES GENERALES ---
import Home from "./components/home.jsx";
import ScrollToTop from "./components/partails/scrollToTop.jsx";
import CursosPage from "./components/partails/CursosPage.jsx";
import NoticiasPage from "./components/partails/NoticiasPage.jsx";
import Us from "./components/partails/Us.jsx";
import AdmissionPage from "./components/partails/AdmissionPage.jsx";
import CertificateValidationPage from "./components/partails/CertificateValidationPage.jsx";
import Login from "./components/login.jsx";
import NotFoundPage from "./components/NotFoundPage.jsx";
import PoliticasPage from "./components/partails/PoliticasPage.jsx";
import UnauthorizedPage from "./components/UnauthorizedPage.jsx";
import ForcePasswordChange from "./components/ForcePasswordChange.jsx";
import RecoverPassword from "./components/RecoverPassword.jsx";
import ResetPassword from "./components/ResetPassword.jsx";
import HomeFooter from "./components/home/home-footer";
import HomeNav from "./components/home/home-nav";
import RequireAuth from "./auth/RequireAuth";
import { getConfig } from "./api/service";
import { BreadcrumbProvider } from "./context/BreadcrumbContext";
import Breadcrumbs from "./components/Breadcrumbs";

// Carga perezosa para rutas pesadas (mejora rendimiento inicial)
const ClassRoom = lazy(() => import("./components/virtual-classroom.jsx"));
const CourseDescrip = lazy(
	() => import("./components/partails/course-descrip.jsx"),
);
const ExamTakingView = lazy(
    () => import("./components/student/classroom/ExamTakingView.jsx")
);
const NewsDescrip = lazy(
	() => import("./components/partails/news-descrip.jsx"),
);
const StudentPortal = lazy(() => import("./components/studentportal.jsx"));
const Info = lazy(() => import("./components/student/portal/info.jsx"));
const Profile = lazy(() => import("./components/student/portal/Profile.jsx"));

// --- COMPONENTE LAYOUT ---
// Este componente decide qué mostrar dependiendo de la URL
function LayoutWrapper({ children, configData, isLoading }) {
	const location = useLocation();

	// Definimos qué rutas NO deben mostrar el Nav/Footer del Home
	const isLogin = location.pathname.startsWith("/login");
	const isStudentPortal = location.pathname.startsWith("/portal");
	const isCompanyPortal = location.pathname.startsWith("/portal-emp");
	const isTeacherPortal = location.pathname.startsWith("/portal-doc");
	const isBackoffice = location.pathname.startsWith("/backoffice");
	const isPasswordChange = location.pathname.startsWith("/cambiar-clave");
	// Si la ruta es cualquiera de estas, ocultamos el Nav y Footer del Home
	const hideHomeNavAndFooter =
		isStudentPortal ||
		isCompanyPortal ||
		isTeacherPortal ||
		isBackoffice ||
		isLogin ||
		isPasswordChange;

	// estados de los menus
	const [activeMenu, setActiveMenu] = useState(null);
	const toggleMenu = (menuName) =>
		setActiveMenu(activeMenu === menuName ? null : menuName);

	return (
		<>
			{/* Si NO debemos ocultarlo, mostramos el footer del home */}
			{!hideHomeNavAndFooter && (
				<nav className="layout-container font-n">
					<HomeNav activeMenu={activeMenu} toggleMenu={toggleMenu} />
				</nav>
			)}

			{/* Contenido principal de la ruta (Home, Noticias, etc.) */}
			<main>
				{!hideHomeNavAndFooter && <Breadcrumbs />}
				{children}
			</main>

			{/* Si NO debemos ocultarlo, mostramos el footer del home */}
			{!hideHomeNavAndFooter && (
				<HomeFooter footerData={configData} isLoading={isLoading} />
			)}
		</>
	);
}

// --- COMPONENTE APP PRINCIPAL ---
export function App() {
	const [configData, setConfigData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const notFoundImage = `${process.env.PUBLIC_URL}/img/404_not_found.png`;

	// Cargamos la configuración global una sola vez
	useEffect(() => {
		const loadConfig = async () => {
			try {
				const data = await getConfig();

				// MAGIA AQUÍ: Forzamos a que el objeto se convierta en una lista.
				setConfigData(Array.isArray(data) ? data : [data]);
			} catch (error) {
				console.error("Error cargando configuración global:", error);
			} finally {
				setIsLoading(false);
			}
		};
		loadConfig();
	}, []);

	useEffect(() => {
		const handleImageResourceError = (event) => {
			const target = event.target;
			if (!(target instanceof HTMLImageElement)) {
				return;
			}

			if (target.dataset.fallbackApplied === "1") {
				return;
			}

			if (
				target.currentSrc?.includes("/img/404_not_found.png") ||
				target.src?.includes("/img/404_not_found.png")
			) {
				target.dataset.fallbackApplied = "1";
				return;
			}

			target.dataset.fallbackApplied = "1";
			target.src = notFoundImage;
		};

		// Captura errores de recursos que no hacen bubbling en el DOM.
		window.addEventListener("error", handleImageResourceError, true);
		return () => {
			window.removeEventListener("error", handleImageResourceError, true);
		};
	}, [notFoundImage]);

	return (
        <BreadcrumbProvider>
		<Router>
			<ScrollToTop />
			<LayoutWrapper configData={configData} isLoading={isLoading}>
				<Suspense
					fallback={
						<div className="d-flex justify-content-center align-items-center min-vh-100">
							<p className="text-muted">Cargando...</p>
						</div>
					}
				>
					<Routes>
						{/* Raiz de la página*/}
						<Route
							path="/"
							element={<Home configData={configData} />}
						/>
						{/* Plantilla con todos los cursos y todas las noticias */}
						<Route path="/cursos" element={<CursosPage />} />
						<Route path="/curso/:id" element={<CourseDescrip footerData={configData} />} />
						<Route path="/noticias" element={<NoticiasPage />} />
						<Route path="/noticia/:id" element={<NewsDescrip />} />
						<Route path="/admision" element={<AdmissionPage />} />
						<Route
							path="/validar-certificados"
							element={<CertificateValidationPage />}
						/>
						<Route path="/nosotros" element={<Us configData={configData} isLoading={isLoading} />} />

						{/* Rutas de Login: Comparten el mismo componente */}
						<Route path="/login" element={<Login />} />
						<Route path="/login-emp" element={<Login />} />
						<Route path="/login-doc" element={<Login />} />
						<Route path="/login-est" element={<Login />} />
						<Route path="/recuperar-clave" element={<RecoverPassword />} />
						<Route path="/reset-password" element={<ResetPassword />} />

						<Route
							element={<RequireAuth allowMustChangePassword />}
						>
							<Route path="/cambiar-clave" element={<ForcePasswordChange />} />
						</Route>

						{/* Portal ESTUDIANTE */}
						<Route
							element={
								<RequireAuth
									allowedRoles={[
										"Student",
										"Admin",
										"Teacher",
									]}
									loginPath="/login-est"
								/>
							}
						>
							<Route path="/portal" element={<StudentPortal footerData={configData} isLoading={isLoading} />}>
								<Route
									index
									element={
										<Suspense
											fallback={
												<div className="portal-profile-loading">
													Cargando...
												</div>
											}
										>
											<Info />
										</Suspense>
									}
								/>
								<Route
									path="perfil"
									element={
										<Suspense
											fallback={
												<div className="portal-profile-loading">
													Cargando...
												</div>
											}
										>
											<Profile />
										</Suspense>
									}
								/>
								<Route
									path="academico"
									element={<ClassRoom footerData={configData} isLoading={isLoading} />}
								/>
                                <Route 
                                    path="runs/:runId/examen-taking/:attemptId" 
                                    element={
                                        <Suspense fallback={<div className="text-center p-5">Cargando examen...</div>}>
                                            <ExamTakingView />
                                        </Suspense>
                                    } 
                                />
									<Route
										path="capacitacion"
										element={
											<Suspense
												fallback={
													<div className="portal-profile-loading">
														Cargando...
													</div>
												}
											>
												<InstructivoAdminPro />
											</Suspense>
										}
									/>
							</Route>
						</Route>

						{/* BACKOFFICE (ADMIN) */}
						<Route
							element={
								<RequireAuth
									allowedRoles={["Admin"]}
									loginPath="/login"
								/>
							}
						>
							<Route
								path="/backoffice"
								element={<BackofficeLayout footerData={configData} isLoading={isLoading}/>}
							>
								<Route index element={<AdminDashboard />} />
								<Route
									path="companies"
									element={<CompaniesList />}
								/>
								<Route
									path="companies/:id"
									element={<CompanyDetail />}
								/>
								<Route path="users" element={<UsersList />} />
								<Route
									path="courses"
									element={<CoursesList />}
								/>
								<Route
									path="courses/:templateId/contenido"
									element={<CourseStructurePage />}
								/>
								<Route path="talks" element={<TalksList />} />
								<Route
									path="talks/:id"
									element={<TalksDetail />}
								/>
								<Route
									path="recorded-classes"
									element={<RecordedClassesList />}
								/>
								<Route
									path="final-exams"
									element={<FinalExamsAdmin />}
								/>
								<Route path="news" element={<NewsList />} />
								<Route
									path="carousel"
									element={<CarouselList />}
								/>
								<Route path="web-config" element={<WebConfigPage />} />
								<Route path="instructivo" element={<InstructivoAdminPro />} />
							</Route>
						</Route>
						{/* PORTAL ORGANIZACIÓN */}
						<Route
							element={
								<RequireAuth
									allowedRoles={["CompanyAdmin"]}
									loginPath="/login-emp"
								/>
							}
						>
							<Route
								path="/portal-emp"
								element={<CompanyPortal footerData={configData} isLoading={isLoading}/>}
							>
								<Route index element={<CompanyDashboard />} />
								<Route
									path="cargas"
									element={<UploadStudents />}
								/>
								<Route
									path="matricula"
									element={<EnrollBatch />}
								/>
								<Route
									path="estudiantes"
									element={<MyStudents />}
								/>
								<Route
									path="upload-students"
									element={<UploadStudents />}
								/>
								<Route
									path="enroll-batch"
									element={<EnrollBatch />}
								/>
								<Route
									path="perfil-empresa"
									element={<ProfilePortalEmp />}
								/>
								<Route path="capacitacion" element={<InstructivoAdminPro />} />
							</Route>
						</Route>
						{/* PORTAL DOCENTE */}
						<Route
							element={
								<RequireAuth
									allowedRoles={["Teacher"]}
									loginPath="/login-doc"
								/>
							}
						>
							<Route
								path="/portal-doc"
								element={<TeacherLayout footerData={configData} isLoading={isLoading}/>}
							>
								<Route index element={<TeacherDashboard />} />
								<Route
									path="perfil"
									element={<TeacherProfile />}
								/>
								<Route
									path="cursos/:runId"
									element={<TeacherCourseDetail />}
								/>
								<Route path="capacitacion" element={<InstructivoAdminPro />} />
							</Route>
						</Route>
						<Route path="/politicas" element={<PoliticasPage />} />
						<Route path="/403" element={<UnauthorizedPage />} />
						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</Suspense>
			</LayoutWrapper>
		</Router>
        </BreadcrumbProvider>
	);
}
